<script setup lang="ts">
import { Form, router } from "@inertiajs/vue3"
import { PlusIcon } from "lucide-vue-next"
import { ref } from "vue"

import HeadingSmall from "@/components/HeadingSmall.vue"
import DatePicker from "@/components/custom/DatePicker.vue"
import FileInput from "@/components/custom/FileInput.vue"
import FormField from "@/components/custom/FormField.vue"
import Table from "@/components/custom/Table.vue"
import TableAction from "@/components/custom/TableAction.vue"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { dateFormatter } from "@/lib/formatters"
import {
  familyInsurancePriceByAgeInsurancePath,
  familyMembersInsurancePath,
} from "@/routes"
import type { InsurancesShow } from "@/types/serializers"

type FamilyMember = InsurancesShow["family_members"][number]

defineProps<InsurancesShow>()

const parentescoItems = ["Cónyuge", "Pareja de hecho", "Hijo/a"].map(
  (label) => ({ label, value: label }),
)

const fullAge = ref<number | null>(null)
const priceByBirthDate = ref<number | null>(null)
const priceByBirthDateFetching = ref(false)
const deactivatingDni = ref<string | null>(null)
const today = new Date()
const maxDate = new Date(
  today.getFullYear(),
  today.getMonth(),
  today.getDate() - 1,
)

const columns = ref([
  {
    key: "relationship",
    header: "Parentesco",
  },
  {
    key: "full_name",
    header: "Miembroso",
  },
  {
    key: "price",
    header: "Price",
    right: true,
  },
  {
    key: "active_from",
    header: "Active from",
  },
  {
    key: "active_before",
    header: "Active till",
  },
  // {
  //   key: "status",
  //   header: "Estado",
  //   thClass: "text-center w-fit",
  //   tdClass: "text-center w-fit",
  // },
  {
    id: "action",
    key: "id",
    right: true,
    header: "Acción",
    thClass: "w-fit",
    tdClass: "w-fit",
  },
])

const tableActions = ref([
  {
    label: "Deactivate",
    action: "deactivate",
    disabled: (row: FamilyMember) =>
      row.status === "pending" || deactivatingDni.value !== null,
    loading: (row: FamilyMember) => deactivatingDni.value === row.dni,
    onClick: disableMemberAction,
  },
])

async function disableMemberAction(row: FamilyMember) {
  if (deactivatingDni.value) return

  deactivatingDni.value = row.dni

  router.delete(familyMembersInsurancePath(), {
    data: { dni: row.dni },
    preserveScroll: true,
    preserveState: true,
    onFinish: () => {
      deactivatingDni.value = null
    },
  })
}

const isOpen = ref(false)

function onClose() {
  priceByBirthDateFetching.value = false
  priceByBirthDate.value = null
  fullAge.value = null
  isOpen.value = false
}

async function fetchPriceByAge(birthDate: string | Date | null | undefined) {
  if (priceByBirthDateFetching.value || !birthDate) return
  priceByBirthDateFetching.value = true
  try {
    const age = new Date().getFullYear() - new Date(birthDate).getFullYear()
    fullAge.value = age
    const response = await fetch(
      familyInsurancePriceByAgeInsurancePath({ age }),
    )
    const data = (await response.json()) as { price: number | null }
    priceByBirthDate.value = data.price
  } catch {
    return null
  } finally {
    priceByBirthDateFetching.value = false
  }
}
</script>

<template>
  <Card>
    <CardContent>
      <HeadingSmall title="Miembros del sector de seguros" />
      <div class="mb-4 flex w-full justify-between">
        <p>
          El coste del familiar se descuenta de tu nómina según la edad.<br />
          Ejemplo: Hijo de 10 años = {{ price_for_example_by_age }}€/mes.<br />
          El descuento empieza el mes anterior al alta.
        </p>

        <a
          href="/PRIMAS_2026_FAMILIARES_COMPLETA_Y_EMPLEADOS_Y_FAMILIARES_EXTRA_1.pdf"
          class="text-primary h-fit text-right underline-offset-2 hover:underline"
          target="_blank"
        >
          Consultar Primaspor Edad
        </a>
      </div>

      <div class="flex flex-col gap-2">
        <Table :data="family_members" :columns="columns">
          <template #relationship-cell="{ row }">
            {{
              row?.relationship
                ? parentescoItems.find((p) => p.value === row.relationship)
                    ?.label
                : "—"
            }}
          </template>

          <template #active_from-cell="{ row }">
            {{ dateFormatter(row?.active_from, {}, "date") || "—" }}
          </template>
          <template #active_before-cell="{ row }">
            {{ dateFormatter(row?.active_before, {}, "date") || "—" }}
          </template>
          <template #action-cell="{ row }">
            <TableAction :action="tableActions" :row="row" />
          </template>
        </Table>
        <Button v-if="!isOpen" class="w-fit" @click="isOpen = !isOpen">
          <PlusIcon />
          Agregar
        </Button>
      </div>

      <Form
        v-if="isOpen"
        :action="familyMembersInsurancePath()"
        method="POST"
        class="mt-4"
        disable-while-processing
        :options="{
          preserveScroll: true,
          preserveState: true,
        }"
        @success="onClose"
        #default="{ processing, errors }"
      >
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-4">
            <p class="text-primary text-lg font-bold">
              Adherir Familiar a Adeslas
            </p>
            <FormField
              label="Nombre completo"
              required
              :errors="errors.full_name"
            >
              <Input name="full_name" type="text" />
            </FormField>

            <FormField label="DNI" required :errors="errors.dni">
              <Input name="dni" type="text" />
            </FormField>

            <FormField
              label="Fecha de nacimiento"
              required
              :errors="errors.birth_date"
            >
              <DatePicker
                @update:model-value="fetchPriceByAge"
                :loading="priceByBirthDateFetching"
                :disabled="priceByBirthDateFetching"
                :max-date="maxDate"
                name="birth_date"
              />
              <template #message>
                <span
                  v-if="priceByBirthDate"
                  :class="{ 'text-muted-foreground': priceByBirthDateFetching }"
                >
                  Precio según edad: {{ priceByBirthDate }}€/mes
                </span>
              </template>
            </FormField>

            <FormField
              label="Parentesco"
              required
              :errors="errors.relationship"
            >
              <Select name="relationship">
                <SelectTrigger class="w-full">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    v-for="item in parentescoItems"
                    :key="item.label"
                    :value="item.value"
                  >
                    {{ item.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <div class="flex flex-col gap-4">
            <p class="text-primary text-lg font-bold">Documentos</p>

            <FormField
              name="enrollment_form"
              type="file"
              label="Formulario de Alta Adeslas"
              :errors="errors.enrollment_form"
              required
            >
              <FileInput
                name="enrollment_form"
                accept="image/*,application/pdf"
              />
            </FormField>

            <FormField
              name="health_questionnaire"
              type="file"
              label="Test de Salud (si llevas +3 meses)"
              :errors="errors.health_questionnaire"
              required
            >
              <FileInput
                name="health_questionnaire"
                accept="image/*,application/pdf"
              />
            </FormField>
          </div>
        </div>

        <div class="flex gap-4">
          <Button
            type="submit"
            size="lg"
            class="mt-4 w-fit"
            :disabled="processing"
          >
            <Spinner v-if="processing" class="animate-spin" />
            Confirmar Adhesión
          </Button>
          <Button
            size="lg"
            variant="outline"
            class="mt-4 w-fit"
            :disabled="processing"
            @click="onClose"
          >
            Cancelar
          </Button>
        </div>
      </Form>
    </CardContent>
  </Card>
</template>
